/**
 * content-unstarted.js
 */

import React from 'react';
import {
  styled,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Typography,
  FormControl,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  Badge,
  Link,
} from '@mui/material';
import { UserContext } from 'contexts/user/user-context';
import Icon from 'components/icon';
import ConfirmDialog from 'components/confirm-dialog';
import { LiveTrackingContext } from '../live-tracking/live-tracking-context';
import { TrackingFormContext, TRACK_VISIBILITY } from '../live-tracking/tracking-form-context';
import { TRACK_TYPE, TRACK_TYPE_ICON } from '../tracking-common';


const TrackTypeOption = React.forwardRef((props, ref) => {
  const { type, title, subtitle, badgeValue, ...rest } = props;

  return (
    <ToggleButton
      ref={ref}
      value={type}
      {...rest}
      sx={{ 
        p: 1, 
        '&.Mui-selected, &.Mui-selected:hover': {
          backgroundColor: 'sys.primaryContainer'
        }
      }}
    >
      <Badge 
        badgeContent={badgeValue} 
        color='secondary' 
        sx={{
          flex: 1,
          justifyContent: 'center',
          '& .MuiBadge-badge': {
            top: 3,
            right: 4,
            transform: 'scale(0.8) translate(50%, -50%)',
            border: theme => `1px solid ${theme.palette.sys.primary}`,
            display: badgeValue ? 'flex' : 'none',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', }}>
          <Icon type={TRACK_TYPE_ICON[type]} />
          <Box sx={{ ml: 0.5, display: 'flex', flexDirection: 'column', textAlign: 'left' }}>          
            <Typography 
              variant='subtitle1' 
              sx={{ fontSize: 'sizes.0'}}
            >
              {title}
            </Typography>
            <Typography 
              variant='subtitle2' 
              sx={{ fontSize: 'sizes.1', lineHeight: 1.3, textTransform: 'none', whiteSpace: 'nowrap', }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>
      </Badge>
    </ToggleButton>
  )
})


const TrackTypeToggle = (props) => {
  const { user } = React.useContext(UserContext);
  const { trackType, setTrackType, availableLeadTracks } = React.useContext(TrackingFormContext);
  const { anonTrackCreatedCount } = React.useContext(LiveTrackingContext);
  const availableLeadTracksCount = availableLeadTracks ? availableLeadTracks.length - 1 : null;


  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      value={trackType}
      onChange={(e, v) => {
        if (v && v !== trackType) {
          setTrackType(v);
        }
      }}
      sx={{ backgroundColor: 'sys.surfaceContainerHigh', mb: 2, }}
    >
      <TrackTypeOption 
        type={TRACK_TYPE.DEPLOYMENT}
        title='Deployment'
        subtitle='Track'
        disabled={!user || !user.role.isHandler}
      />
      <TrackTypeOption 
        type={TRACK_TYPE.LEAD}
        title='Training'
        subtitle='Lay Track'
      />
      <TrackTypeOption 
        type={TRACK_TYPE.FOLLOW}
        title='Training'
        subtitle='Follow'
        badgeValue={!!user ? availableLeadTracksCount : anonTrackCreatedCount}
        disabled={!user || !user.role.isHandler}
      />
    </ToggleButtonGroup>
  );
}


const FormBox = (props) => {
  const { infoText } = props;
  const { user } = React.useContext(UserContext);
  const { setIsTrackHelpOpen } = React.useContext(LiveTrackingContext);


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: user ? 320 : 120 }}>
      <InfoText>
        {infoText}{' '}
        <Link 
          sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }} 
          onClick={() => setIsTrackHelpOpen(true)}
        >
          LEARN MORE
        </Link>
      </InfoText>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: 300 }}>
        {props.children}
      </Box>
    </Box>
  );
}


const InfoText = styled(Typography)(({ theme }) => ({
  display: 'block',
  fontSize: theme.typography.sizes[1],
  lineHeight: 1.3,
  textAlign: 'center',
  marginBottom: theme.spacing(4),
}));


const Menu = (props) => {
  const { label, value, onChange, options, ...rest } = props;
  const idPart = label.replace(' ', '-');

  return (
    <FormControl fullWidth size='small' {...rest}>
      <InputLabel id={`tracking-menu-${idPart}`}>{label}</InputLabel>
      <Select
        label={label}
        labelId={`tracking-menu-${idPart}`}
        value={value ?? ''}
        onChange={(e, v) => onChange(v.props.value)}
      >
        {(options ?? []).map(option => (
          <MenuItem key={option.id} value={option} disabled={option.isDisabled}>{option.label || option.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}


const DogMenu = (props) => {
  const { availableDogs, dog, setDog } = React.useContext(TrackingFormContext);

  return (
    <Menu
      label='Dog'
      value={dog}
      onChange={setDog}
      options={availableDogs}
      {...props}
    />
  );
}


const FormDeployment = (props) => {
  return (
    <FormBox
      infoText={<>
        <b>Follow a track for an active deployment. </b>
        A new deployment record will be created automatically.
        Supervisors can view your track as it's created.
      </>}
    >
      <DogMenu />
    </FormBox>
  );
}


const FormLeadTrack = (props) => {
  const { trackName, setTrackName } = React.useContext(TrackingFormContext);

  return (
    <FormBox
      infoText={<>
        <b>Lay a training track for someone to follow. </b>
        A laid track remains available to follow for up to 3 days.
      </>}
    >
      <TextField
        size='small'
        label='Laid Track Name'
        fullWidth
        value={trackName}
        onChange={(e) => setTrackName(e.target.value)}
      />
    </FormBox>
  )
}


const LeadTrackVisibilityOption = (props) => {
  const { startIcon, children, ...rest } = props;

  return (
    <ToggleButton
      sx={{ 
        p: 1, 
        textTransform: 'none',
        fontSize: 'sizes.1',
        borderTop: 0,
        borderBottom: 0,
        '&.Mui-selected, &.Mui-selected:hover': {
          backgroundColor: 'sys.primaryContainer'
        },
        '&.MuiToggleButtonGroup-firstButton': {
          borderLeft: 0,
        },
        '&.MuiToggleButtonGroup-lastButton': {
          borderRight: 0
        },
        '&.Mui-disabled': {
          border: 'unset'
        }
      }}
      {...rest}
    >
      <Box 
        className='flex-hc' 
        sx={{ 
          '& .MuiSvgIcon-root': { 
            fontSize: 'sizes.3',
            mr: 0.5 
          }
        }}
      >
        {startIcon}
        {children}
      </Box>
    </ToggleButton>
  )
}


const LeadTrackVisibilityToggle = (props) => {
  const { followLeadTrack, trackVisibility, setFollowLeadTrackVisibility } = React.useContext(TrackingFormContext);
  const [confirmVisible, setConfirmVisible] = React.useState();
  const visibilityValue = trackVisibility?.[followLeadTrack?.id] ?? TRACK_VISIBILITY.HIDDEN;

  return (
    <>
      <ToggleButtonGroup
        exclusive
        fullWidth
        disabled={followLeadTrack?.id === null}
        value={followLeadTrack?.id 
          ? visibilityValue ?? TRACK_VISIBILITY.HIDDEN
          : null}
        onChange={async (e, v) => {
          if (v && v !== visibilityValue) {
            if (v !== TRACK_VISIBILITY.VISIBLE || await confirmVisible()) {
              setFollowLeadTrackVisibility(v);
            }
          }
        }}
        sx={{ 
          backgroundColor: 'sys.surfaceContainerHigh', 
          border: theme => `1px solid ${theme.palette.sys.outlineVariant}`,
          mb: 2, 
        }}
      >
        <LeadTrackVisibilityOption 
          value={TRACK_VISIBILITY.HIDDEN} 
          children='Hidden' 
          disabled={visibilityValue === TRACK_VISIBILITY.VISIBLE}
        />
        <LeadTrackVisibilityOption 
          value={TRACK_VISIBILITY.START_LOCATION} 
          children={<>Start&nbsp;Location</>} 
          disabled={visibilityValue === TRACK_VISIBILITY.VISIBLE}
        />
        <LeadTrackVisibilityOption 
          value={TRACK_VISIBILITY.VISIBLE} 
          children='Visible' 
          startIcon={<Icon type='warning-circle' sx={{ color: 'warning.main' }} />}
        />
      </ToggleButtonGroup>

      <ConfirmDialog
        titleStyle="warning"
        title='Confirm Track Visibility'
        titleIcon='warning'
        content={<>
          This action will reveal the track layer's path and your exercise record will indicate that you knew this information. 
          This can't be undone. Press OK to proceed.
          <br/><br/>
          <Icon
            type='tip'
            fontSize='tiny'
            sx={{ color: 'comp.tip.icon', mr: 0.5 }}
          />
          TIP: There's nothing wrong with knowing the track layer's path while following a track. 
          In fact, it's good practice to follow a mix of known and unknown tracks during training.
        </>}
        setConfirm={setConfirmVisible}
      />
    </>
  );
}


const FormFollowTrack = (props) => {
  const { 
    availableExercises, 
    followExercise, 
    setFollowExercise,
    availableLeadTracks,
    followLeadTrack,
    setFollowLeadTrack,
  } = React.useContext(TrackingFormContext);

  return (
    <>
      <FormBox
        infoText={<>
          <b>Follow a previously laid training track. </b>
          The start of the laid track must be within 500 yards of your current position.
          {/*Trainers can view your track as it's created.*/}
        </>}
      >
        <Menu
          label='Tracking Exercise'
          value={followExercise}
          onChange={setFollowExercise}
          options={availableExercises}
          sx={{ mb: 3 }}
        />
        <DogMenu 
          sx={{ mb: 3 }}
        />
        <Menu
          label='Laid Track To Follow'
          value={followLeadTrack}
          onChange={setFollowLeadTrack}
          options={availableLeadTracks}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <InputLabel shrink sx={{ pl: 2 }}>Laid Track Visibility</InputLabel>
          <LeadTrackVisibilityToggle />
        </Box>
      </FormBox>
    </>
  )
}


const FORMS = {
  [TRACK_TYPE.DEPLOYMENT]: <FormDeployment />,
  [TRACK_TYPE.LEAD]: <FormLeadTrack />,
  [TRACK_TYPE.FOLLOW]: <FormFollowTrack />
};


export default function ContentUnstarted(props) {
  const { trackType } = React.useContext(TrackingFormContext);

  return (
    <>
      <TrackTypeToggle />
      {FORMS[trackType]}
    </>
  );
}